'use client';

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { useThemeContext } from '@/theme/ThemeProvider';

interface VoiceCommandContextType {
  isListening: boolean;
  isSupported: boolean;
  transcript: string;
  error: string | null;
  startListening: () => Promise<void>;
  stopListening: () => Promise<void>;
  clearTranscript: () => void;
  speakResponse: (text: string) => void;
  processCommand: (command: string) => Promise<void>;
  // Conversational AI features
  isConversationalMode: boolean;
  setConversationalMode: (enabled: boolean) => void;
  conversationId: string | null;
  aiResponse: string | null;
  isProcessing: boolean;
  sendToAI: (message: string) => Promise<string | null>;
}

const VoiceCommandContext = createContext<VoiceCommandContextType | undefined>(undefined);

interface VoiceCommandProviderProps {
  children: React.ReactNode;
}

export function VoiceCommandProvider({ children }: VoiceCommandProviderProps) {
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [recognition, setRecognition] = useState<any>(null);
  const [isSupported, setIsSupported] = useState(false);
  const [speechSynthesis, setSpeechSynthesis] = useState<SpeechSynthesis | null>(null);
  const [voicePreferences, setVoicePreferences] = useState<any>(null);
  const [userWakeWords, setUserWakeWords] = useState<string[]>(['listen pi', 'hey pi', 'pi app']);
  // Conversational AI state
  const [isConversationalMode, setIsConversationalMode] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [aiResponse, setAiResponse] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const router = useRouter();
  const themeCtx = useThemeContext();

  // Load user voice preferences
  useEffect(() => {
    const loadVoicePreferences = async () => {
      try {
        const response = await fetch('/api/voice/preferences');
        if (response.ok) {
          const preferences = await response.json();
          setVoicePreferences(preferences);
          setUserWakeWords(preferences.wakeWords || ['listen pi', 'hey pi', 'pi app']);
        }
      } catch (error) {
        console.log('Failed to load voice preferences, using defaults');
      }
    };

    loadVoicePreferences();
  }, []);

  // Initialize speech recognition and synthesis
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const SpeechRecognition = window.SpeechRecognition || (window as any).webkitSpeechRecognition;

      if (SpeechRecognition) {
        setIsSupported(true);
        const recognitionInstance = new SpeechRecognition();

        recognitionInstance.continuous = true;
        recognitionInstance.interimResults = true;
        recognitionInstance.lang = 'en-US';

        recognitionInstance.onstart = () => {
          setIsListening(true);
          setError(null);
        };

        recognitionInstance.onend = () => {
          setIsListening(false);
        };

        recognitionInstance.onresult = (event: any) => {
          let finalTranscript = '';
          let interimTranscript = '';

          for (let i = event.resultIndex; i < event.results.length; i++) {
            const transcript = event.results[i][0].transcript;
            if (event.results[i].isFinal) {
              finalTranscript += transcript;
            } else {
              interimTranscript += transcript;
            }
          }

          if (finalTranscript) {
            setTranscript(finalTranscript);
            processCommand(finalTranscript);
          }
        };

        recognitionInstance.onerror = (event: any) => {
          console.error('Speech recognition error:', event.error);
          setError(`Speech recognition error: ${event.error}`);
          setIsListening(false);
        };

        setRecognition(recognitionInstance);
      }

      // Initialize speech synthesis
      if ('speechSynthesis' in window) {
        setSpeechSynthesis(window.speechSynthesis);
      }
    }
  }, []);

  useEffect(() => {
    function handleThemeChange(e: CustomEvent) {
      if (e.detail && e.detail.mode) {
        themeCtx.setThemeMode(e.detail.mode);
      }
    }
    function handleModuleColorChange(e: CustomEvent) {
      if (e.detail && e.detail.module && e.detail.color) {
        themeCtx.setModuleColor(e.detail.module, e.detail.color);
      }
    }
    function handleResetModuleColors() {
      themeCtx.resetModuleColors();
    }
    window.addEventListener('theme-change', handleThemeChange as EventListener);
    window.addEventListener('module-color-change', handleModuleColorChange as EventListener);
    window.addEventListener('reset-module-colors', handleResetModuleColors);
    return () => {
      window.removeEventListener('theme-change', handleThemeChange as EventListener);
      window.removeEventListener('module-color-change', handleModuleColorChange as EventListener);
      window.removeEventListener('reset-module-colors', handleResetModuleColors);
    };
  }, [themeCtx]);

  const processCommand = useCallback(async (command: string) => {
    const lowerCommand = command.toLowerCase();

    try {
      // Check if voice control is enabled
      if (voicePreferences && !voicePreferences.enabled) {
        return;
      }

      // Check for wake words first (using dynamic user wake words)
      let hasWakeWord = false;
      let cleanCommand = lowerCommand;

      for (const wakeWord of userWakeWords) {
        if (lowerCommand.includes(wakeWord.toLowerCase())) {
          hasWakeWord = true;
          cleanCommand = lowerCommand.replace(wakeWord.toLowerCase(), '').trim();
          break;
        }
      }

      // If no wake word detected, ignore the command
      if (!hasWakeWord) {
        return;
      }

      // Process enhanced commands via API
      try {
        const response = await fetch('/api/voice/command', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ command: cleanCommand })
        });

        if (response.ok) {
          const result = await response.json();
          if (result.success) {
            speakResponse(result.message);
            return;
          }
        }
      } catch (apiError) {
        console.log('API command failed, falling back to local processing');
      }

      // Fallback to local navigation commands
      if (cleanCommand.includes('go to') || cleanCommand.includes('open') || cleanCommand.includes('show')) {
        if (cleanCommand.includes('dashboard') || cleanCommand.includes('home')) {
          router.push('/dashboard');
          speakResponse('Navigating to dashboard');
        } else if (cleanCommand.includes('finance') || cleanCommand.includes('money')) {
          router.push('/finance');
          speakResponse('Opening finance module');
        } else if (cleanCommand.includes('insurance')) {
          router.push('/insurance');
          speakResponse('Opening insurance module');
        } else if (cleanCommand.includes('property') || cleanCommand.includes('house')) {
          router.push('/property');
          speakResponse('Opening property module');
        } else if (cleanCommand.includes('warranty')) {
          router.push('/warranty');
          speakResponse('Opening warranty module');
        } else if (cleanCommand.includes('grocery') || cleanCommand.includes('shopping')) {
          router.push('/grocery-saver');
          speakResponse('Opening grocery saver');
        } else if (cleanCommand.includes('ai') || cleanCommand.includes('chat') || cleanCommand.includes('assistant')) {
          router.push('/ai-assistant');
          speakResponse('Opening AI assistant');
        } else if (cleanCommand.includes('admin') || cleanCommand.includes('settings')) {
          router.push('/admin');
          speakResponse('Opening admin panel');
        }
      }

      // Action commands
      else if (cleanCommand.includes('add account') || cleanCommand.includes('new account')) {
        router.push('/finance/add-account');
        speakResponse('Opening add account form');
      } else if (cleanCommand.includes('add policy') || cleanCommand.includes('new policy')) {
        router.push('/insurance/new-policy');
        speakResponse('Opening new policy form');
      } else if (cleanCommand.includes('add property') || cleanCommand.includes('new property')) {
        router.push('/property/add');
        speakResponse('Opening add property form');
      } else if (cleanCommand.includes('add item') || cleanCommand.includes('add to grocery')) {
        router.push('/grocery-saver');
        speakResponse('Opening grocery list to add items');
      }

      // Utility commands
      else if (cleanCommand.includes('refresh') || cleanCommand.includes('reload')) {
        window.location.reload();
        speakResponse('Refreshing the page');
      } else if (cleanCommand.includes('back') || cleanCommand.includes('go back')) {
        window.history.back();
        speakResponse('Going back');
      } else if (cleanCommand.includes('forward') || cleanCommand.includes('go forward')) {
        window.history.forward();
        speakResponse('Going forward');
      }

      // Module-specific commands
      else if (cleanCommand.includes('check balance') || cleanCommand.includes('show balance')) {
        speakResponse('Let me check your account balance');
        // This will be handled by the enhanced API processor
      } else if (cleanCommand.includes('file claim') || cleanCommand.includes('insurance claim')) {
        router.push('/insurance');
        speakResponse('Opening insurance to file a claim');
      } else if (cleanCommand.includes('find deals') || cleanCommand.includes('grocery deals')) {
        router.push('/grocery-saver');
        speakResponse('Opening grocery saver to find deals');
      } else if (cleanCommand.includes('property value') || cleanCommand.includes('check property')) {
        router.push('/property');
        speakResponse('Opening property module to check values');
      } else if (cleanCommand.includes('warranty status') || cleanCommand.includes('check warranty')) {
        router.push('/warranty');
        speakResponse('Opening warranty module to check status');
      }

      // Help command
      else if (cleanCommand.includes('help') || cleanCommand.includes('what can you do')) {
        speakResponse('I can help you check account balances, pay bills, navigate modules, file claims, and more. Try saying "Listen Pi, check my account balance" or "Listen Pi, pay gas bill of $300"');
      }

      // Stop listening command
      else if (cleanCommand.includes('stop listening') || cleanCommand.includes('stop voice')) {
        await stopListening();
        speakResponse('Stopping voice recognition');
      }

      // Default response
      else {
        speakResponse('I heard you say ' + command + '. Try saying "help" to see what I can do');
      }

    } catch (err) {
      console.error('Error processing voice command:', err);
      setError('Failed to process command');
      speakResponse('Sorry, I encountered an error processing your command');
    }
  }, [router, userWakeWords, voicePreferences]);

  const speakResponse = useCallback((text: string) => {
    if (speechSynthesis && voicePreferences?.enabled !== false) {
      // Cancel any ongoing speech
      speechSynthesis.cancel();

      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = voicePreferences?.speed || 1.0;
      utterance.pitch = voicePreferences?.pitch || 1.0;
      utterance.volume = voicePreferences?.volume || 0.8;

      // Find a good voice based on user language preference
      const voices = speechSynthesis.getVoices();
      const userLang = voicePreferences?.language || 'en';

      const preferredVoice = voices.find(voice =>
        voice.lang.includes(userLang) && voice.name.includes('Female')
      ) || voices.find(voice => voice.lang.includes(userLang)) || voices[0];

      if (preferredVoice) {
        utterance.voice = preferredVoice;
      }

      speechSynthesis.speak(utterance);
    }
  }, [speechSynthesis, voicePreferences]);

  const startListening = useCallback(async () => {
    if (recognition && !isListening) {
      try {
        setTranscript('');
        setError(null);
        await recognition.start();
      } catch (err) {
        console.error('Error starting speech recognition:', err);
        setError('Failed to start listening');
      }
    }
  }, [recognition, isListening]);

  const stopListening = useCallback(async () => {
    if (recognition && isListening) {
      try {
        await recognition.stop();
      } catch (err) {
        console.error('Error stopping speech recognition:', err);
        setError('Failed to stop listening');
      }
    }
  }, [recognition, isListening]);

  const clearTranscript = useCallback(() => {
    setTranscript('');
  }, []);

  /**
   * Send message to AI for conversational processing
   * Uses the voice/conversation API for full AI integration
   */
  const sendToAI = useCallback(async (message: string): Promise<string | null> => {
    setIsProcessing(true);
    setAiResponse(null);

    try {
      const response = await fetch('/api/voice/conversation', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          // Send text directly for text-based conversation
          audio: null,
          userId: 'user', // In production, get from auth context
          conversationId,
          generateVoiceResponse: false,
          textInput: message, // Alternative text input
        }),
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setAiResponse(result.response);
          if (result.metadata?.conversationId) {
            setConversationId(result.metadata.conversationId);
          }
          // Speak the response if voice is enabled
          if (voicePreferences?.enabled !== false) {
            speakResponse(result.response);
          }
          return result.response;
        }
      }
      return null;
    } catch (err) {
      console.error('Error sending to AI:', err);
      setError('Failed to process AI request');
      return null;
    } finally {
      setIsProcessing(false);
    }
  }, [conversationId, voicePreferences, speakResponse]);

  const value = {
    isListening,
    isSupported,
    transcript,
    error,
    startListening,
    stopListening,
    clearTranscript,
    speakResponse,
    processCommand,
    // Conversational AI features
    isConversationalMode,
    setConversationalMode: setIsConversationalMode,
    conversationId,
    aiResponse,
    isProcessing,
    sendToAI,
  };

  return (
    <VoiceCommandContext.Provider value={value}>
      {children}
    </VoiceCommandContext.Provider>
  );
}

export function useVoiceCommand() {
  const context = useContext(VoiceCommandContext);
  if (context === undefined) {
    throw new Error('useVoiceCommand must be used within a VoiceCommandProvider');
  }
  return context;
}
